if (!customElements.get('sc-pot-upsell-widget')) {
  class ScPotUpsellWidget extends HTMLElement {
    constructor() {
      super();

      this.mainProductPrice = 0;
      this.mainProductComparePrice = 0;
      this.mainProductWeight = 0;
      this.mainProductRequiresShipping = true;
      this.variantChangeUnsubscriber = null;
      this.onItemClick = this.onItemClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    connectedCallback() {
      this.syncInitialState();
      this.addEventListener('click', this.onItemClick);
      document.addEventListener('click', this.onDocumentClick);
      this.updatePrice();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onItemClick);
      document.removeEventListener('click', this.onDocumentClick);

      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
        this.variantChangeUnsubscriber = null;
      }
    }

    get items() {
      return Array.from(this.querySelectorAll('[data-upsell-item]'));
    }

    get activeItem() {
      return this.querySelector('[data-upsell-item][data-active="true"]');
    }

    syncInitialState() {
      const currentItem = this.querySelector('[data-current-product]');
      const activeItem = this.activeItem || currentItem || this.items[0];

      this.items.forEach((item) => {
        item.dataset.active = item === activeItem ? 'true' : 'false';
      });

      this.setMainPricesFromItem(activeItem);
    }

    onItemClick(event) {
      const colorDot = event.target.closest('[data-color-dot]');

      if (colorDot) {
        event.preventDefault();
        event.stopPropagation();

        const item = colorDot.closest('[data-upsell-item]');

        if (!item || colorDot.disabled) return;

        this.applyVariantSelection(item, colorDot);
        this.selectItem(item);
        return;
      }

      const item = event.target.closest('[data-upsell-item]');

      if (!item || !this.contains(item)) return;

      if (event.target.closest('[data-info-icon]')) {
        event.preventDefault();
        event.stopPropagation();
        this.openPopup(item);
        return;
      }

      this.selectItem(item);
    }

    applyVariantSelection(item, source) {
      const variantId = source.dataset.variantId;
      const variantPrice = ScPotUpsellWidget.toCents(source.dataset.price);
      const variantCompareAtPrice = ScPotUpsellWidget.toCents(source.dataset.compareAtPrice);
      const variantWeight = ScPotUpsellWidget.toNumber(source.dataset.weight);
      const variantRequiresShipping = source.dataset.requiresShipping !== 'false';
      const variantImage = source.dataset.image;

      item.dataset.variantId = variantId;
      item.dataset.firstVariantPrice = variantPrice;
      item.dataset.firstVariantCompareAtPrice = variantCompareAtPrice;
      item.dataset.firstVariantWeight = variantWeight;
      item.dataset.firstVariantRequiresShipping = variantRequiresShipping;

      const imageElement = item.querySelector('[data-upsell-image]');

      if (imageElement && variantImage) {
        imageElement.src = variantImage;
      }

      this.updateItemPriceDisplay(item, variantPrice, variantCompareAtPrice);
      this.updateColorDots(item, variantId);
    }

    updateColorDots(item, variantId) {
      const dots = item.querySelectorAll('[data-color-dots] [data-color-dot]');

      dots.forEach((dot) => {
        dot.classList.toggle('sc-pot_upsell_item-color-dot--active', dot.dataset.variantId === variantId);
      });
    }

    updateItemPriceDisplay(item, variantPrice, variantCompareAtPrice) {
      const priceContainer = item.querySelector('[data-price-container]');

      if (!priceContainer) return;

      const hasComparePrice = variantCompareAtPrice > variantPrice;
      const compareHtml = hasComparePrice
        ? `<span class="sc-pot_upsell_item-compare-price"><s>+ ${ScPotUpsellWidget.formatMoney(variantCompareAtPrice)}</s></span>`
        : '';

      priceContainer.innerHTML = `${compareHtml}<span class="sc-pot_upsell_item-current-price" data-current-price>+ ${ScPotUpsellWidget.formatMoney(variantPrice)}</span>`;
    }

    onDocumentClick(event) {
      const popup = event.target.closest('[data-pot-info-popup][data-sc-popup-owner]');
      const closeButton = popup ? event.target.closest('[data-popup-close]') : null;
      const selectButton = popup ? event.target.closest('[data-popup-select-button]') : null;

      if (closeButton) {
        this.closePopup(popup);
        return;
      }

      if (selectButton) {
        event.preventDefault();

        const item = this.findItemByPopup(popup);

        if (item) this.selectItem(item);

        this.closePopup(popup);
        return;
      }

      if (popup && event.target === popup) this.closePopup(popup);
    }

    selectItem(selectedItem) {
      this.items.forEach((item) => {
        item.dataset.active = item === selectedItem ? 'true' : 'false';
      });

      this.updatePrice();
    }

    openPopup(item) {
      const popup = item.querySelector('[data-pot-info-popup]') || document.querySelector(`[data-sc-popup-owner="${item.dataset.variantId}"]`);

      if (!popup) return;

      popup.dataset.scPopupOwner = item.dataset.variantId || '';
      document.body.appendChild(popup);
      document.body.classList.add('overflow-hidden');
      popup.classList.remove('hidden');
    }

    closePopup(popup) {
      if (!popup) return;

      popup.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');
    }

    findItemByPopup(popup) {
      if (!popup?.dataset.scPopupOwner) return null;

      return this.querySelector(`[data-upsell-item][data-variant-id="${popup.dataset.scPopupOwner}"]`);
    }

    onVariantChange(event) {
      const variant = event?.data?.variant;

      if (!variant) return;
      const shippingNotice = document.querySelector('sc-product-shipping-notice');
      const shippingVariant =
        typeof shippingNotice?.getVariant === 'function' ? shippingNotice.getVariant(variant) : null;

      this.mainProductPrice = ScPotUpsellWidget.toCents(variant.price);
      this.mainProductComparePrice = ScPotUpsellWidget.toCents(variant.compare_at_price);
      this.mainProductWeight = ScPotUpsellWidget.toNumber(
        shippingVariant?.weight ?? variant.weight ?? variant.grams
      );
      this.mainProductRequiresShipping =
        (shippingVariant?.requiresShipping ?? variant.requires_shipping ?? variant.requiresShipping) !== false;

      this.items.forEach((item) => {
        item.dataset.mainProductPrice = this.mainProductPrice;
        item.dataset.mainProductComparePrice = this.mainProductComparePrice;
        item.dataset.mainProductWeight = this.mainProductWeight;
        item.dataset.mainProductRequiresShipping = this.mainProductRequiresShipping;
      });

      const currentItem = this.querySelector('[data-current-product]');

      if (currentItem) currentItem.dataset.variantId = variant.id;

      this.updatePrice();
    }

    setMainPricesFromItem(item) {
      if (!item) return;

      this.mainProductPrice = ScPotUpsellWidget.toCents(item.dataset.mainProductPrice);
      this.mainProductComparePrice = ScPotUpsellWidget.toCents(item.dataset.mainProductComparePrice);
      this.mainProductWeight = ScPotUpsellWidget.toNumber(item.dataset.mainProductWeight);
      this.mainProductRequiresShipping = item.dataset.mainProductRequiresShipping !== 'false';
    }

    updatePrice() {
      const activeItem = this.activeItem;

      if (!activeItem) return;
      if (!this.mainProductPrice) this.setMainPricesFromItem(activeItem);

      const isCurrentProduct = activeItem.hasAttribute('data-current-product');
      const upsellPrice = isCurrentProduct ? 0 : ScPotUpsellWidget.toCents(activeItem.dataset.firstVariantPrice);
      const upsellComparePrice = isCurrentProduct
        ? 0
        : ScPotUpsellWidget.toCents(activeItem.dataset.firstVariantCompareAtPrice);
      const upsellWeight = isCurrentProduct ? 0 : ScPotUpsellWidget.toNumber(activeItem.dataset.firstVariantWeight);
      const upsellRequiresShipping = !isCurrentProduct && activeItem.dataset.firstVariantRequiresShipping !== 'false';
      const totalPrice = this.mainProductPrice + upsellPrice;
      const totalWeight = this.mainProductWeight + upsellWeight;
      const totalRequiresShipping = this.mainProductRequiresShipping || upsellRequiresShipping;
      const effectiveUpsellComparePrice = upsellComparePrice > upsellPrice ? upsellComparePrice : upsellPrice;
      const totalComparePrice =
        this.mainProductComparePrice > this.mainProductPrice
          ? this.mainProductComparePrice + effectiveUpsellComparePrice
          : this.mainProductPrice + effectiveUpsellComparePrice;

      this.updatePriceElements(totalPrice, totalComparePrice);
      this.updateShippingNotice(totalPrice, totalWeight, totalRequiresShipping);
    }

    updatePriceElements(totalPrice, totalComparePrice) {
      const priceRoot =
        document.querySelector('.product__info-container .price__container') ||
        document.querySelector('.product__info-container .price') ||
        document.querySelector('.price__container');

      if (!priceRoot) return;

      const priceElement =
        priceRoot.querySelector('.sc-price__regular .price-item--sale') ||
        priceRoot.querySelector('.price__sale .price-item--sale') ||
        priceRoot.querySelector('.price__regular .price-item--regular') ||
        priceRoot.querySelector('.price-item--regular');
      const comparePriceElement =
        priceRoot.querySelector('.sc-price-item--compare') ||
        priceRoot.querySelector('.price__sale .price-item--regular');

      if (priceElement) priceElement.textContent = ScPotUpsellWidget.formatMoney(totalPrice);

      if (comparePriceElement && totalComparePrice > totalPrice) {
        comparePriceElement.textContent = ScPotUpsellWidget.formatMoney(totalComparePrice);
      }

      this.updateSaleBadge(totalPrice, totalComparePrice);
    }

    updateSaleBadge(totalPrice, totalComparePrice) {
      const saleBadge = document.querySelector('.product__info-container .sc-price__badge-sale');

      if (!saleBadge || !totalComparePrice || totalComparePrice <= totalPrice) return;

      const discountPercentage = Math.round(((totalComparePrice - totalPrice) * 100) / totalComparePrice);
      const discountTemplate = saleBadge.getAttribute('data-discount-template') || '';

      if (discountTemplate) saleBadge.textContent = discountTemplate.replace('[discount]', discountPercentage);
    }

    updateShippingNotice(totalPrice, totalWeight, requiresShipping) {
      const detail = {
        price: totalPrice,
        weight: totalWeight,
        requiresShipping,
      };

      document.dispatchEvent(new CustomEvent('sc:shipping-notice:update', { detail }));

      this.dispatchEvent(
        new CustomEvent('sc:pot-upsell:change', {
          bubbles: true,
          detail,
        })
      );
    }

    static toCents(value) {
      const cents = parseInt(value, 10);

      return Number.isNaN(cents) ? 0 : cents;
    }

    static toNumber(value) {
      const number = Number(value);

      return Number.isFinite(number) ? number : 0;
    }

    static formatMoney(amount) {
      const formatted = (ScPotUpsellWidget.toCents(amount) / 100).toFixed(2).replace('.', ',');

      return `€${formatted}`;
    }
  }

  customElements.define('sc-pot-upsell-widget', ScPotUpsellWidget);
}
